/**
 * Design Token Utilities
 *
 * Type-safe helpers for using design system tokens
 * Ensures consistent color usage across the application
 */

/**
 * Sentiment types for visual feedback
 */
export type Sentiment = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

/**
 * Sentiment color mappings for text
 */
export const sentimentText: Record<Sentiment, string> = {
  success: 'text-success-600',
  warning: 'text-warning-600',
  danger: 'text-danger-500',
  info: 'text-primary-500',
  neutral: 'text-foreground',
};

/**
 * Sentiment color mappings for backgrounds
 */
export const sentimentBg: Record<Sentiment, string> = {
  success: 'bg-success-50',
  warning: 'bg-warning-50',
  danger: 'bg-danger-50',
  info: 'bg-primary-50',
  neutral: 'bg-muted',
};

/**
 * Sentiment color mappings for borders
 */
export const sentimentBorder: Record<Sentiment, string> = {
  success: 'border-success-200',
  warning: 'border-warning-200',
  danger: 'border-danger-200',
  info: 'border-primary-200',
  neutral: 'border-border',
};

/**
 * Sentiment color mappings for accent borders (left border on KPI cards)
 * Uses stronger -500 colors for visual impact
 */
export const sentimentAccentBorder: Record<Sentiment, string> = {
  success: 'border-l-success-500',
  warning: 'border-l-warning-500',
  danger: 'border-l-danger-500',
  info: 'border-l-primary-400',
  neutral: 'border-l-neutral-200',
};

/**
 * Sentiment color mappings for trend pill backgrounds
 * Used in KPI cards for trend indicators
 */
export const sentimentTrendPill: Record<'up' | 'down' | 'neutral', string> = {
  up: 'bg-success-50 text-success-700',
  down: 'bg-danger-50 text-danger-600',
  neutral: 'bg-muted text-muted-foreground',
};

/**
 * Sentiment color mappings for icon backgrounds
 * Used in KPI cards and similar components
 */
export const sentimentIconBg: Record<Sentiment, string> = {
  success: 'bg-success-50 text-success-600',
  warning: 'bg-warning-50 text-warning-600',
  danger: 'bg-danger-50 text-danger-500',
  info: 'bg-primary-50 text-primary-500',
  neutral: 'bg-muted text-muted-foreground',
};

/**
 * Combined sentiment color utilities
 * Provides all color variations for a sentiment in one object
 */
export const sentimentColors: Record<
  Sentiment,
  {
    text: string;
    bg: string;
    border: string;
    iconBg: string;
  }
> = {
  success: {
    text: sentimentText.success,
    bg: sentimentBg.success,
    border: sentimentBorder.success,
    iconBg: sentimentIconBg.success,
  },
  warning: {
    text: sentimentText.warning,
    bg: sentimentBg.warning,
    border: sentimentBorder.warning,
    iconBg: sentimentIconBg.warning,
  },
  danger: {
    text: sentimentText.danger,
    bg: sentimentBg.danger,
    border: sentimentBorder.danger,
    iconBg: sentimentIconBg.danger,
  },
  info: {
    text: sentimentText.info,
    bg: sentimentBg.info,
    border: sentimentBorder.info,
    iconBg: sentimentIconBg.info,
  },
  neutral: {
    text: sentimentText.neutral,
    bg: sentimentBg.neutral,
    border: sentimentBorder.neutral,
    iconBg: sentimentIconBg.neutral,
  },
};

/**
 * Chart color palette based on design system
 * Use these for consistent chart colors
 */
export const chartColors = {
  primary: 'var(--color-primary-500)',
  success: 'var(--color-success-500)',
  warning: 'var(--color-warning-500)',
  danger: 'var(--color-danger-500)',
  neutral: 'var(--color-neutral-500)',

  // Named chart colors for specific use cases
  optimal: 'var(--color-success-500)',
  low: 'var(--color-warning-500)',
  stockout: 'var(--color-danger-500)',
  overstock: 'var(--color-primary-500)',
};

/**
 * Chart color array for multi-series charts
 */
export const chartPalette = [
  'var(--color-primary-500)',
  'var(--color-accent)',
  'var(--color-success-500)',
  'var(--color-warning-500)',
  'var(--color-danger-500)',
  'var(--color-primary-700)',
  'var(--color-accent-foreground)',
];

/**
 * Get sentiment from a numeric value
 * Useful for automatic sentiment detection
 */
export function getSentimentFromValue(
  value: number,
  thresholds: { danger: number; warning: number; success: number }
): Sentiment {
  if (value <= thresholds.danger) return 'danger';
  if (value <= thresholds.warning) return 'warning';
  if (value >= thresholds.success) return 'success';
  return 'neutral';
}

/**
 * Get sentiment from percentage
 * Common thresholds: <50% danger, <75% warning, >=90% success
 */
export function getSentimentFromPercentage(percentage: number): Sentiment {
  return getSentimentFromValue(percentage, {
    danger: 50,
    warning: 75,
    success: 90,
  });
}

/**
 * Spacing Scale Constants
 * Based on 8px grid system - use these instead of arbitrary values
 * Ensures consistent spacing across all components and pages
 */
export const spacing = {
  // Card gaps
  cardGap: 'gap-5', // 20px - between KPI cards
  cardGapLg: 'gap-6', // 24px - between larger cards

  // Page spacing
  pageGap: 'gap-5', // 20px - between page sections
  pageGapLg: 'gap-8', // 32px - between major sections

  // Card internal padding
  cardPadding: 'p-5', // 20px - KPI card padding
  cardPaddingLg: 'p-6', // 24px - larger card padding

  // Table spacing
  tableCellPaddingX: 'px-4', // 16px horizontal
  tableCellPaddingY: 'py-3', // 12px vertical
} as const;

/**
 * Border radius scale
 * Consistent rounding for all UI elements
 */
export const radius = {
  card: 'rounded-lg', // 12px - cards
  button: 'rounded-md', // 8px - buttons, inputs
  badge: 'rounded-full', // pills
  dialog: 'rounded-xl', // 16px - modals
} as const;
